import {
  CanvasConversationActionEnvelopeSchema,
  CanvasConversationInspectEnvelopeSchema,
  conseilDuVerbeCanvasRetire,
  estUnVerbeCanvasRetire,
  type CanvasConversationActionEnvelope,
  type CanvasConversationInspectEnvelope,
  type CanvasRetiredActionName,
  type CanvasSuperDocResultPayload,
} from '@hector/shared-types';
import type { SuperDocInstance } from '@superdoc/react';
import { memeEpoque, type JetonDEpoque } from './canvas-agent/executionAsynchrone';
import type { CanvasDocumentRuntimeCoordinator } from './canvasDocumentRuntime';
import {
  executeSuperDocCanvasRequest,
  type CanvasExecutionResources,
  type GardeDEpoque,
} from './superDocCanvasExecutor';

/**
 * L'enveloppe agent dans la SEULE forme que ce navigateur accepte (#1336).
 *
 * ⚠️ Pas l'union de transition (`CanvasAgentRequestEnvelopeTransitionSchema`),
 * qui accepte aussi la forme legacy : ce bundle annonce ses montages en forme
 * conversation, le courtier lui rend donc la forme conversation, et accepter la
 * forme legacy ne servirait qu'à laisser passer une commande que plus rien ne
 * sait router. Les deux schémas sont `.strict()` : une enveloppe qui porterait
 * AUSSI `documentSessionId` est refusée — l'alias que la spec interdit ne peut
 * pas entrer par cette porte.
 */
type CanvasConversationRequestEnvelope = CanvasConversationInspectEnvelope | CanvasConversationActionEnvelope;

/**
 * Le runtime, réduit à ce que la garde en consomme.
 *
 * La couture est délibérément étroite : six méthodes, toutes adressées par
 * `conversationId`. C'est elle qui rend les gardes de ce module observables
 * sans monter registre, directory ni SuperDoc.
 */
export type CanvasAgentRuntime = Pick<CanvasDocumentRuntimeCoordinator, 'requireReady' | 'getSessionForConversation' | 'getScope' | 'enqueueMutation' | 'refreshRevision' | 'syncRevisionFromEditor'>;

type CanvasRequestExecutor = (
  instance: SuperDocInstance | null,
  request: CanvasConversationRequestEnvelope,
  dependances: { garde: GardeDEpoque; resources: CanvasExecutionResources },
) => Promise<CanvasSuperDocResultPayload>;

/**
 * L'époque réellement montée pour cette conversation, relue à l'instant (#939).
 *
 * Une session en cours de montage (`state !== 'ready'`) rend `null` : c'est un
 * document qui n'est pas celui sur lequel l'action a été autorisée, et un jeton
 * qui « ressemble » suffirait à laisser passer une mutation.
 */
function epoqueMontee(runtime: CanvasAgentRuntime, conversationId: string): JetonDEpoque | null {
  const session = runtime.getSessionForConversation(conversationId);
  if (!session || session.state !== 'ready') return null;
  return { conversationId: session.conversationId, mountEpoch: session.mountEpoch };
}

/** Le verbe annoncé par la trame, lu UNE fois — avant tout schéma. */
function lireLeVerbe(payload: unknown): 'canvas_inspect' | 'canvas_perform_action' | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const tool = (payload as { tool?: unknown }).tool;
  if (tool === 'canvas_inspect' || tool === 'canvas_perform_action') return tool;
  return null;
}

function actionName(payload: CanvasConversationRequestEnvelope): string {
  return payload.tool === 'canvas_inspect' ? 'inspect' : payload.action.action;
}

function payloadActionName(payload: unknown): string {
  if (typeof payload !== 'object' || payload === null) return 'unknown';
  const record = payload as Record<string, unknown>;
  if (record.tool === 'canvas_inspect') return 'inspect';
  if (record.tool === 'canvas_perform_action' && typeof record.action === 'object' && record.action !== null) {
    const action = (record.action as { action?: unknown }).action;
    return typeof action === 'string' ? action : 'invalid_action';
  }
  return 'unknown';
}

/**
 * Refus d'une action dont le verbe a été retiré du catalogue.
 *
 * Seul un backend antérieur au retrait l'envoie encore (déploiement en cours) :
 * le refus d'enveloppe ordinaire listerait les verbes valides sans dire que
 * celui-ci n'existe plus, ni quoi faire à sa place. Le conseil est celui que
 * rend le backend à jour : il a une source, `CANVAS_RETIRED_ACTIONS`.
 */
function verbeRetireReceipt(payload: Record<string, unknown>, verbe: CanvasRetiredActionName): CanvasSuperDocResultPayload {
  // Le nom du verbe retiré voyage comme une chaîne, à l'égal de tout verbe hors
  // catalogue d'une enveloppe refusée : le backend qui l'a envoyé le connaît.
  const action: string = verbe;
  return {
    requestId: typeof payload.requestId === 'string' ? payload.requestId : 'invalid-envelope',
    tool: 'canvas_perform_action',
    action,
    ok: false,
    code: 'CANVAS_AGENT_ENVELOPE_INVALID',
    error: `canvas_perform_action ${verbe} refused before the mutation queue and Document API call: `
      + `${conseilDuVerbeCanvasRetire(verbe)} Nothing was written.`,
  } as CanvasSuperDocResultPayload;
}

function invalidEnvelopeReceipt(payload: unknown, issues: string): CanvasSuperDocResultPayload {
  const request = typeof payload === 'object' && payload !== null ? payload as Record<string, unknown> : {};
  return {
    requestId: typeof request.requestId === 'string' ? request.requestId : 'invalid-envelope',
    tool: request.tool === 'canvas_perform_action' ? 'canvas_perform_action' : 'canvas_inspect',
    action: payloadActionName(payload),
    ok: false,
    code: 'CANVAS_AGENT_ENVELOPE_INVALID',
    error: `canvas_superdoc_request refused before Document API call: invalid canvas agent envelope (${issues}). `
      + 'The backend must inject the mount identity (conversationId + mountEpoch — both required, and never alongside a '
      + 'documentSessionId) plus expectedRevision/snapshotToken on canvas_perform_action; the model should only provide '
      + 'canvas_inspect or canvas_perform_action input.',
  } as CanvasSuperDocResultPayload;
}

/** Décrit un montage dans un message lu hors contexte, `null` compris. */
function decrireLeMontage(jeton: JetonDEpoque | null): string {
  return jeton === null
    ? 'aucun montage prêt'
    : `conversationId=${jeton.conversationId} mountEpoch=${jeton.mountEpoch}`;
}

/**
 * Refus PRÉALABLE : la commande a été autorisée sur un montage qui n'est plus
 * celui de cette conversation.
 *
 * ⚠️ Distinct de {@link montageDepasseApresAppel}, et les deux formulations
 * comptent : ici rien n'a été tenté, donc l'agent peut réinspecter et rejouer ;
 * là-bas une mutation a pu aboutir, et rejouer la dupliquerait.
 *
 * ⚠️ La conversation, elle, n'est plus comparée : elle EST la clé du registre
 * et du directory depuis #1336 (lot N), donc « le propriétaire trouvé
 * correspond-il à celui qu'annonce la trame ? » est une tautologie — un capteur
 * qui rendrait la même valeur avec et sans le défaut. Seule l'époque tranche.
 */
function montageDepasseAvantAppel(
  payload: CanvasConversationRequestEnvelope,
  attendue: JetonDEpoque,
  courante: JetonDEpoque | null,
): CanvasSuperDocResultPayload {
  return {
    requestId: payload.requestId,
    tool: payload.tool,
    action: actionName(payload),
    ok: false,
    code: 'DOCUMENT_SESSION_CHANGED',
    error:
      `${payload.tool} ${actionName(payload)} refused before Document API call: `
      + `the command was authorised on ${decrireLeMontage(attendue)}, but that mount is gone — `
      + `this conversation now runs ${decrireLeMontage(courante)}. Nothing was written. `
      + 'Run canvas_inspect against the current mount before acting: block references captured on the previous '
      + 'mount no longer address anything.',
  } as CanvasSuperDocResultPayload;
}

function staleSnapshotReceipt(
  payload: CanvasConversationActionEnvelope,
  revisionCourante: string | null,
): CanvasSuperDocResultPayload {
  return {
    requestId: payload.requestId,
    tool: payload.tool,
    action: payload.action.action,
    ok: false,
    code: 'STALE_CANVAS_REVISION',
    error:
      `canvas_perform_action ${payload.action.action} refused before Document API call: ` +
      `expectedRevision=${payload.expectedRevision} but current revision=${revisionCourante}. ` +
      'Run canvas_inspect again and retry with selectors from the fresh snapshot.',
  } as CanvasSuperDocResultPayload;
}

/**
 * Une ancienne enveloppe reste décodable pendant le rolling deploy, mais ne
 * porte pas la preuve qu'une projection native finale a publié les identités
 * qu'elle veut muter. La laisser entrer dans la file ferait de son absence une
 * autorisation implicite de viser tout bloc brut (#1605).
 */
function inspectionScopeManquanteReceipt(
  payload: CanvasConversationActionEnvelope,
): CanvasSuperDocResultPayload {
  return {
    requestId: payload.requestId,
    tool: payload.tool,
    action: payload.action.action,
    ok: false,
    code: 'CANVAS_INSPECTION_REQUIRED',
    error:
      `canvas_perform_action ${payload.action.action} refused before the mutation queue and Document API call: `
      + 'this legacy action envelope omits inspectedPublicNodeIds, so it cannot prove which native final-projection '
      + 'blocks are safe to mutate. Nothing was read or written. Run canvas_inspect with projection="final" from the '
      + 'current Canvas bundle, then retry only with the action envelope forged from that fresh inspection.',
    recovery: {
      action: 'reinspect',
      reason: 'The action has no native final public-identity scope; inspect the current document before acting.',
    },
  } as CanvasSuperDocResultPayload;
}

/**
 * Verdict APRÈS un appel asynchrone : le montage a changé sous l'action.
 *
 * `mutationMayHaveApplied` départage les deux seules conduites possibles —
 * réinspecter et rejouer, ou réinspecter sans jamais rejouer.
 */
function montageDepasseApresAppel(
  payload: CanvasConversationRequestEnvelope,
  expected: JetonDEpoque,
  current: JetonDEpoque | null,
  mutationMayHaveApplied: boolean,
): CanvasSuperDocResultPayload {
  return {
    requestId: payload.requestId,
    tool: payload.tool,
    action: actionName(payload),
    ok: false,
    code: 'DOCUMENT_SESSION_CHANGED',
    error:
      `${payload.tool} ${actionName(payload)} result discarded after an asynchronous Document API call: `
      + `expected ${decrireLeMontage(expected)}, current ${decrireLeMontage(current)}. `
      + (mutationMayHaveApplied
        ? 'The mutation may have been applied to its routed document; do not replay it blindly. Run canvas_inspect: it reads the document mounted now; continue from what it shows.'
        : 'No mutation was attempted. Run canvas_inspect: it reads the document mounted now; continue from that read.'),
  } as CanvasSuperDocResultPayload;
}

function postSnapshotRevision(result: CanvasSuperDocResultPayload): string | null {
  if (typeof result.data !== 'object' || result.data === null) return null;
  const receipt = (result.data as { receipt?: unknown }).receipt;
  if (typeof receipt !== 'object' || receipt === null) return null;
  const postSnapshot = (receipt as { postSnapshot?: unknown }).postSnapshot;
  if (typeof postSnapshot !== 'object' || postSnapshot === null) return null;
  const revision = (postSnapshot as { revision?: unknown }).revision;
  return typeof revision === 'string' && revision.trim().length > 0 ? revision : null;
}

/** A successful transport receipt also carries failed/aborted/no-op actions. */
function mutationEvidence(result: CanvasSuperDocResultPayload, beforeRevision: string | null): { check: boolean; applied: boolean } {
  const afterRevision = postSnapshotRevision(result);
  if (afterRevision !== null && afterRevision !== beforeRevision) return { check: true, applied: true };
  if (typeof result.data !== 'object' || result.data === null) return { check: false, applied: false };
  if ('mutationMayHaveApplied' in result.data && result.data.mutationMayHaveApplied === true) {
    return { check: true, applied: false };
  }
  const receipt = 'receipt' in result.data ? result.data.receipt : null;
  if (typeof receipt !== 'object' || receipt === null || !('status' in receipt)) return { check: false, applied: false };
  if (receipt.status === 'partial') return { check: true, applied: true };
  return { check: receipt.status === 'non_verifiable', applied: false };
}

function resolveExecutionResources(
  runtime: CanvasAgentRuntime,
  conversationId: string,
): CanvasExecutionResources {
  const scope = runtime.getScope(conversationId);
  if (!scope) {
    throw new Error(
      `Canvas agent request refused for conversationId=${conversationId}: scope=missing. `
      + 'Run canvas_inspect: it reads the document mounted for this conversation now; never substitute the selected canvas.',
    );
  }
  const resources = scope.getPhysicalResources();
  return {
    reviewEnabled: resources.reviewEnabled,
    // Le scope EXACT de la conversation routée : c'est son régime que le bouton
    // de la barre affiche, donc celui que `set_review_mode` doit régler.
    setReviewEnabled: (reviewEnabled) => scope.setReviewEnabled(reviewEnabled),
    // L'état du peintre vit sur ce même scope : lu et tenu à jour à la demande, par chaque écriture de forme.
    peintre: { estArrete: () => scope.peintreEstArrete(), noter: (arrete) => scope.noterLePeintre(arrete) },
    writer: resources.writer,
    // Sans lui, `set_letterhead` refuse TOUJOURS (« aucun canvas monté ») alors que
    // `RedactionCanvas` a bien déposé le résolveur dans ce scope : l'oubli de ce seul
    // champ a rendu le verbe inutilisable par l'agent (constaté le 2026-09-24).
    namedLetterheadResolver: resources.namedLetterheadResolver,
    // Le scope EXACT de la conversation routée, capturé ici et lu à la demande :
    // relire « le scope courant » plus tard pourrait viser un autre montage.
    isHostParked: () => scope.isHostParked(),
  };
}

/**
 * Applies the frontend's mount and revision fences before any Document API call.
 * It is kept outside WebSocketPool so race tests execute the exact production guard.
 */
export async function executeCanvasAgentRequest(
  payload: unknown,
  runtime: CanvasAgentRuntime,
  execute: CanvasRequestExecutor = executeSuperDocCanvasRequest,
): Promise<CanvasSuperDocResultPayload> {
  const verbe = lireLeVerbe(payload);
  if (verbe === null) {
    return invalidEnvelopeReceipt(payload, 'only strict canvas agent envelopes are accepted; legacy canvas_* wire tools were removed');
  }
  const verbeDAction = payloadActionName(payload);
  if (verbe === 'canvas_perform_action' && estUnVerbeCanvasRetire(verbeDAction)) {
    return verbeRetireReceipt(payload as Record<string, unknown>, verbeDAction);
  }

  // Le schéma est choisi par le VERBE, jamais par une union : les messages de
  // refus nomment alors la clé fautive du bon contrat, au lieu de recopier les
  // deux branches d'une union à l'agent.
  const parsed = verbe === 'canvas_inspect'
    ? CanvasConversationInspectEnvelopeSchema.safeParse(payload)
    : CanvasConversationActionEnvelopeSchema.safeParse(payload);
  if (!parsed.success) {
    return invalidEnvelopeReceipt(
      payload,
      parsed.error.issues.map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`).join(' ; '),
    );
  }

  if (parsed.data.tool === 'canvas_inspect') {
    const requete = parsed.data;
    const current = runtime.requireReady(requete.conversationId);
    const executionResources = resolveExecutionResources(runtime, requete.conversationId);
    // #1044 : une inspection est une opération documentaire routée, même si
    // elle ne mute rien. Adopter l'éditeur d'un autre montage en cas de
    // mismatch ferait lire le document d'après à un travail lié immuablement
    // au document d'avant.
    //
    // ⚠️ Une époque ANNONCÉE ne peut plus être absente (#1336) : le contrat de
    // la forme conversation l'exige, une trame qui l'omet est refusée plus haut
    // comme enveloppe invalide. Le `!== undefined` d'avant disait « je ne sais
    // pas, laisse passer » ; il n'existe plus de trame qui ne sache pas.
    if (current.mountEpoch !== requete.mountEpoch) {
      return montageDepasseAvantAppel(
        requete,
        { conversationId: requete.conversationId, mountEpoch: requete.mountEpoch },
        epoqueMontee(runtime, requete.conversationId),
      );
    }
    const attendue: JetonDEpoque = {
      conversationId: current.conversationId,
      mountEpoch: current.mountEpoch,
    };
    const result = await execute(current.instance, requete, {
      garde: {
        attendue,
        lireCourante: () => epoqueMontee(runtime, requete.conversationId),
      },
      resources: executionResources,
    });
    const couranteApresInspection = epoqueMontee(runtime, requete.conversationId);
    if (!memeEpoque(attendue, couranteApresInspection)) {
      return montageDepasseApresAppel(requete, attendue, couranteApresInspection, false);
    }
    // Resynchronise le cache runtime sur la révision que l'inspect vient de LIRE du
    // document : le broker backend fencera la prochaine mutation sur cette valeur,
    // le cache doit donc la connaître aussi (sinon désync cache/document → STALE).
    const inspectedRevision =
      result.ok && typeof result.data === 'object' && result.data !== null &&
      typeof (result.data as { revision?: unknown }).revision === 'string'
        ? (result.data as { revision: string }).revision
        : null;
    if (inspectedRevision) runtime.refreshRevision(requete.conversationId, inspectedRevision);
    return result;
  }

  const actionPayload = parsed.data;
  // Le contrat partagé garde ce champ optionnel UNIQUEMENT pour décoder et
  // diagnostiquer un navigateur antérieur à #1605. Une action moderne ne
  // rejoint jamais la file sans la portée publiée par l'inspection finale.
  if (actionPayload.inspectedPublicNodeIds === undefined) {
    return inspectionScopeManquanteReceipt(actionPayload);
  }
  return runtime.enqueueMutation(actionPayload.conversationId, async () => {
    const current = runtime.requireReady(actionPayload.conversationId);
    // Entrée RÉELLE de l'action sérialisée : une bascule de régime ou un remount
    // pendant son attente ne doit pas lui laisser le writer/régime capturé à
    // l'arrivée de la frame.
    const executionResources = resolveExecutionResources(runtime, actionPayload.conversationId);
    if (current.mountEpoch !== actionPayload.mountEpoch) {
      // Refus PRÉALABLE : ce callback est celui d'un montage révoqué, et il
      // n'a rien tenté — le dire « peut-être appliqué » ferait renoncer l'agent
      // à un travail qu'il peut reprendre après une inspection.
      return montageDepasseAvantAppel(
        actionPayload,
        { conversationId: actionPayload.conversationId, mountEpoch: actionPayload.mountEpoch },
        epoqueMontee(runtime, actionPayload.conversationId),
      );
    }
    // Fence sur la révision RÉELLE du document, pas sur le cache runtime : SuperDoc peut
    // bumper sa révision APRÈS le re-read d'un receipt (mutation « failed-but-applied »),
    // laissant le cache en retard d'un cran alors que l'inspect suivant — et donc
    // l'expectedRevision du broker — lit déjà la vraie valeur. Comparer au cache produisait
    // un STALE_CANVAS_REVISION inescapable (conv 6b3d5ec4 : 8 refus en boucle).
    const liveRevision = (await runtime.syncRevisionFromEditor(actionPayload.conversationId)) ?? current.revision;
    if (liveRevision !== actionPayload.expectedRevision) {
      return staleSnapshotReceipt(actionPayload, liveRevision);
    }
    // L'ÉPOQUE capturée au début du callback, portée dans le contexte d'action
    // et ré-assertée à chaque frontière d'`await` par l'exécuteur (#939).
    const attendue: JetonDEpoque = {
      conversationId: current.conversationId,
      mountEpoch: current.mountEpoch,
    };
    const result = await execute(current.instance, actionPayload, {
      garde: {
        attendue,
        lireCourante: () => epoqueMontee(runtime, actionPayload.conversationId),
      },
      resources: executionResources,
    });
    const couranteApresMutation = epoqueMontee(runtime, actionPayload.conversationId);
    if (!memeEpoque(attendue, couranteApresMutation)) {
      return montageDepasseApresAppel(actionPayload, attendue, couranteApresMutation, true);
    }
    const nextRevision = postSnapshotRevision(result);
    // Ré-assertion AVANT le rafraîchissement de révision : poser la révision
    // d'un montage sur la session d'un autre ferait refuser en boucle les
    // mutations suivantes, sans que rien ne nomme la cause. `refreshRevision`
    // fence déjà sur la conversation ; on ne s'appuie pas dessus — la garde
    // doit être lisible là où elle protège.
    // La comparaison passe par `memeEpoque`, jamais par une réécriture sur
    // place : le jour où `JetonDEpoque` gagne un champ, il n'y a qu'UN endroit
    // à mettre à jour. Une seconde écriture du même contrôle serait celle qu'on
    // oublierait — et poser la révision d'un montage sur la session d'un autre
    // est très exactement ce que ce bloc existe pour empêcher.
    if (nextRevision) {
      runtime.refreshRevision(actionPayload.conversationId, nextRevision);
    }
    // Start one checkpoint while this exclusive action still owns the exact
    // revision. `checkpoint()` starts the export synchronously, whereas its
    // validation, PUT and acknowledgement deliberately run in the draft
    // controller after this tool receipt returns. An applied mutation and a
    // durable DOCX are separate facts: a later save failure must not rewrite
    // the already verified editing result as a failed Canvas action (#1607).
    //
    // Its own mutation activity is exempted; a stream or native writer remains
    // an absolute export prohibition. The controller owns status/error/retry,
    // so its promise is observed here only to prevent an unhandled rejection.
    const controller = runtime.getScope(actionPayload.conversationId)?.getDraftController();
    const mutation = mutationEvidence(result, liveRevision);
    if (controller && mutation.check) {
      controller.markDirty();
      void controller.checkpoint({ afterCompletedAgentMutation: true }).catch(() => undefined);
    }
    return result;
  });
}
